import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/meta";
import { CTABand, EditorialMarquee, ParallaxImage, Reveal, SectionDivider, SectionHeader } from "../components/Chrome";

const LINE_EASE = [0.22, 1, 0.36, 1];

const GATEWAY_TONES = ["bg-[#3A3A3A]", "bg-[#413A40]", "bg-[#36363B]", "bg-[#3E3838]", "bg-[#38403C]"];

function HeroLine({ text, delay, className = "" }) {
  return (
    <div className="overflow-hidden">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1, delay, ease: LINE_EASE }}
        className={`block ${className}`}
      >
        {text}
      </motion.span>
    </div>
  );
}

export default function Home() {
  const { data } = useQuery({
    queryKey: ["content", "home"],
    queryFn: async () => (await api.get("/content/home")).data,
  });
  const c = data?.data;
  usePageMeta("Klyx™ — AI Agency Hub", c?.hero?.sub);
  if (!c) return <div data-testid="home-loading" className="min-h-screen bg-ink" />;

  const { hero, marquee, chapters, gateways, cta } = c;

  return (
    <div data-testid="home-page">
      {/* Kinetic hero */}
      <section className="relative min-h-screen flex items-end overflow-hidden spotlight grid-lines">
        <div className="absolute inset-0">
          <motion.img
            src={hero.image}
            alt=""
            initial={{ opacity: 0, scale: 1.08 }}
            animate={{ opacity: 0.35, scale: 1 }}
            transition={{ duration: 1.6, ease: LINE_EASE }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 pb-20 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            data-testid="hero-eyebrow"
            className="font-mono text-xs tracking-[0.3em] uppercase text-mauve mb-8"
          >
            {hero.eyebrow}
          </motion.p>
          <h1 data-testid="hero-heading" className="font-heading font-black text-5xl sm:text-7xl lg:text-[7rem] tracking-tighter leading-[0.95] text-silver">
            <HeroLine text={hero.line1} delay={0.2} />
            <HeroLine text={hero.line2} delay={0.32} className="text-mauve italic" />
            <HeroLine text={hero.line3} delay={0.44} />
          </h1>
          <div className="mt-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7, ease: LINE_EASE }}
              data-testid="hero-sub"
              className="text-base sm:text-lg text-zinc-200 max-w-xl leading-relaxed"
            >
              {hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: LINE_EASE }}
              className="flex flex-wrap gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-3 bg-mauve text-white px-8 py-4 text-xs tracking-[0.2em] uppercase font-bold transition-[background-color,transform] duration-200 hover:bg-mauve-dark hover:-translate-y-0.5"
              >
                {hero.cta_primary} <ArrowUpRight size={16} />
              </Link>
              <a
                href="#gateways"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-3 border border-white/20 text-zinc-300 px-8 py-4 text-xs tracking-[0.2em] uppercase font-medium transition-[border-color,color,transform] duration-200 hover:border-mauve hover:text-white hover:-translate-y-0.5"
              >
                {hero.cta_secondary} <ArrowDown size={16} />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      <EditorialMarquee items={marquee} />

      {/* Manifesto chapters */}
      <section data-testid="manifesto-section" className="bg-ink">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 lg:py-40">
          <SectionHeader eyebrow="The manifesto" title="Three commitments run through everything we make." testId="manifesto-header" />
          <div className="mt-20">
            {chapters.map((ch, i) => (
              <Reveal key={ch.num} delay={i * 0.08}>
                <div
                  data-testid={`manifesto-chapter-${ch.num}`}
                  className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 border-t-2 border-white/[0.07] py-14 lg:py-20 group"
                >
                  <div className="md:col-span-3">
                    <span className="font-mono text-6xl lg:text-8xl font-bold text-white/10 transition-colors duration-300 group-hover:text-mauve/40">
                      {ch.num}
                    </span>
                  </div>
                  <div className="md:col-span-9 lg:col-span-7">
                    <h3 className="font-heading font-bold text-2xl sm:text-4xl tracking-tight text-silver">{ch.title}</h3>
                    <p className="mt-5 text-base sm:text-lg text-zinc-200 leading-relaxed max-w-2xl">{ch.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Subsite gateways */}
      <section id="gateways" data-testid="gateways-section" className="bg-surface">
        <SectionDivider />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 lg:py-40">
          <SectionHeader eyebrow="The hub" title="Five doors. One studio." testId="gateways-header" />
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
            {gateways.map((g, i) => (
              <Reveal key={g.slug} delay={(i % 2) * 0.1} className={i % 2 === 1 ? "md:mt-16" : ""}>
                <Link
                  to={`/${g.slug}`}
                  data-testid={`gateway-card-${g.slug}`}
                  className={`group block border border-white/15 ${GATEWAY_TONES[i % GATEWAY_TONES.length]} transition-[border-color,transform,box-shadow] duration-300 hover:border-mauve hover:-translate-y-1 hover:shadow-[0_0_50px_rgba(155,31,110,0.3)]`}
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <ParallaxImage src={g.image} alt={g.title} className="w-full h-full" speed={0.08} />
                  </div>
                  <div className="p-8 lg:p-10 flex items-start justify-between gap-6">
                    <div>
                      <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-200 mb-3">
                        0{i + 1} — Practice
                      </p>
                      <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-white group-hover:text-mauve-light transition-colors duration-200">
                        {g.title}
                      </h3>
                      {g.hook && (
                        <p data-testid={`gateway-hook-${g.slug}`} className="mt-2 text-sm sm:text-base font-semibold text-mauve-light">
                          {g.hook}
                        </p>
                      )}
                      <p className="mt-3 text-sm sm:text-base text-zinc-100 leading-relaxed max-w-md">{g.desc}</p>
                    </div>
                    <ArrowUpRight
                      size={28}
                      className="text-zinc-300 shrink-0 transition-[color,transform] duration-300 group-hover:text-mauve-light group-hover:translate-x-1 group-hover:-translate-y-1"
                    />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand cta={cta} />
    </div>
  );
}
